/**
 * @fileoverview Minimal plug-and-play drawer custom element (right side only)
 *
 * @example
 * // Basic usage with internal toggle
 * <drawer-element id="cart-drawer">
 *   <drawer-toggle>
 *     <button>Open</button>
 *   </drawer-toggle>
 *   <drawer-content>
 *     <button class="close">X</button>
 *     <p>Content here</p>
 *   </drawer-content>
 * </drawer-element>
 *
 * @example
 * // External toggle using drawer-id
 * <drawer-toggle drawer-id="my-drawer">
 *   <button>Open Drawer</button>
 * </drawer-toggle>
 *
 * @example
 * // Programmatic control via window events
 * window.dispatchEvent(new Event('open-drawer-my-drawer'));
 * window.dispatchEvent(new Event('close-drawer-my-drawer'));
 */

const DRAWER_STYLES = `
  :host { display: contents; }

  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    justify-content: flex-end;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }

  .backdrop.open {
    visibility: visible;
    opacity: 1;
  }

  .drawer-content {
    background: var(--drawer-bg, #fff);
    height: 100dvh;
    width: var(--drawer-width, auto);
    max-width: 100%;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
  }

  .backdrop.open .drawer-content {
    transform: translateX(0);
  }

  @media (prefers-reduced-motion: reduce) {
    .backdrop, .drawer-content {
      transition: none;
    }
  }
`;

class DrawerElement extends HTMLElement {
  /** @type {HTMLElement|null} */
  #backdrop = null;
  #boundOpen = null;
  #boundClose = null;
  #boundKeydown = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.innerHTML = `
      <style>${DRAWER_STYLES}</style>
      <slot name="toggle"></slot>
      <div class="backdrop">
        <div class="drawer-content">
          <slot name="content"></slot>
        </div>
      </div>
    `;

    // Bind methods for proper cleanup
    this.#boundOpen = () => this.open();
    this.#boundClose = () => this.close();
    this.#boundKeydown = (e) => {
      if (e.key === "Escape") this.close();
    };
  }

  connectedCallback() {
    this.#backdrop = this.shadowRoot.querySelector(".backdrop");

    // Move to body for proper stacking
    if (!this.parentElement?.isSameNode(document.body)) {
      document.body.appendChild(this);
    }

    // Assign slots
    this.querySelector("drawer-toggle")?.setAttribute("slot", "toggle");
    this.querySelector("drawer-content")?.setAttribute("slot", "content");

    // Internal toggle click
    this.querySelector("drawer-toggle")?.addEventListener(
      "click",
      this.#boundOpen,
    );

    // Backdrop click to close
    this.#backdrop?.addEventListener("click", (e) => {
      if (e.target === this.#backdrop) this.close();
    });

    // Close button inside content
    this.querySelector(".close")?.addEventListener("click", this.#boundClose);

    // Window events for external control
    const id = this.id;
    if (id) {
      window.addEventListener(`open-drawer-${id}`, this.#boundOpen);
      window.addEventListener(`close-drawer-${id}`, this.#boundClose);
    }
  }

  disconnectedCallback() {
    const id = this.id;
    if (id) {
      window.removeEventListener(`open-drawer-${id}`, this.#boundOpen);
      window.removeEventListener(`close-drawer-${id}`, this.#boundClose);
    }
    document.removeEventListener("keydown", this.#boundKeydown);
  }

  /** Opens the drawer */
  open() {
    this.#backdrop?.classList.add("open");
    this.setAttribute("open", "");
    this.setAttribute("role", "dialog");
    this.setAttribute("aria-modal", "true");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.#boundKeydown);
    this.dispatchEvent(new CustomEvent("drawer:open", { bubbles: true }));
  }

  /** Closes the drawer */
  close() {
    this.#backdrop?.classList.remove("open");
    this.removeAttribute("open");
    document.removeEventListener("keydown", this.#boundKeydown);

    // Only restore scroll if no other drawers/modals are open
    const openElements = document.querySelectorAll(
      "drawer-element[open], modal-element[open]",
    );
    if (openElements.length === 0) {
      document.body.style.overflow = "";
    }

    this.dispatchEvent(new CustomEvent("drawer:close", { bubbles: true }));
  }

  /** @returns {boolean} Whether the drawer is open */
  get isOpen() {
    return this.#backdrop?.classList.contains("open") ?? false;
  }
}

class DrawerToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", () => {
      const drawerId = this.getAttribute("drawer-id");
      if (!drawerId) return;

      const drawer = document.getElementById(drawerId);
      if (drawer?.isOpen) {
        window.dispatchEvent(new Event(`close-drawer-${drawerId}`));
      } else {
        window.dispatchEvent(new Event(`open-drawer-${drawerId}`));
      }
    });
  }
}

class DrawerContent extends HTMLElement {
  connectedCallback() {
    this.setAttribute("slot", "content");
  }
}

customElements.define("drawer-element", DrawerElement);
customElements.define("drawer-toggle", DrawerToggle);
customElements.define("drawer-content", DrawerContent);
